import { useEffect, useState } from 'react';
import {
  listarPeriodos, abrirPeriodo, fecharPeriodo, reabrirPeriodo,
  listarSnapshots, liberarSnapshot, revogarSnapshot,
} from '../api';
import { PageHeader, Loading, Empty, Alert, Badge } from '../components/common';
import { mesAtual, fmtDataHora } from '../lib/format';
import TravaEscrituracao from '../components/TravaEscrituracao';
import { EncerramentoCarteira } from '../components/CompetenciaFinal';

const MESES = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const STATUS_BADGE = {
  aberto:         { tipo: 'success', label: 'Aberto' },
  em_fechamento:  { tipo: 'warning', label: 'Em fechamento' },
  fechado:        { tipo: 'neutral', label: 'Fechado 🔒' },
  reaberto:       { tipo: 'warning', label: 'Reaberto' },
};

export default function Fechamento({ clienteId, onNavigate }) {
  const [periodos, setPeriodos] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [reabrindo, setReabrindo] = useState(null);   // {ano, mes}
  const [motivo, setMotivo] = useState('');

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    Promise.allSettled([listarPeriodos(clienteId), listarSnapshots(clienteId)])
      .then(([p, s]) => {
        setPeriodos(p.status === 'fulfilled' ? (p.value.items || []) : []);
        setSnapshots(s.status === 'fulfilled' ? (s.value.items || []) : []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [clienteId]);

  if (!clienteId) return <Empty title="Selecione um cliente" />;
  if (loading) return <Loading />;

  const agir = async (fn, okMsg) => {
    try {
      await fn();
      setMsg({ tipo: 'success', txt: okMsg });
      carregar();
    } catch (err) {
      setMsg({ tipo: 'danger', txt: err.message });
    }
  };

  const comp = mesAtual();
  const [anoAtual, mesAtualNum] = comp.split('-').map(Number);
  const temMesAtual = periodos.some(p => p.ano === anoAtual && p.mes === mesAtualNum);

  const confirmarReabertura = async () => {
    if (motivo.trim().length < 5) {
      setMsg({ tipo: 'warning', txt: 'Informe um motivo com pelo menos 5 caracteres — fica registrado na auditoria.' });
      return;
    }
    await agir(
      () => reabrirPeriodo(clienteId, reabrindo.ano, reabrindo.mes, motivo.trim()),
      `Período ${MESES[reabrindo.mes]}/${reabrindo.ano} reaberto.`
    );
    setReabrindo(null);
    setMotivo('');
  };

  return (
    <>
      <PageHeader
        title="Fechamento do Período"
        sub="Período fechado bloqueia novos lançamentos e alterações. Reabertura exige motivo (auditável)."
        actions={!temMesAtual && (
          <button className="primary" onClick={() => agir(
            () => abrirPeriodo(clienteId, anoAtual, mesAtualNum),
            `Período ${MESES[mesAtualNum]}/${anoAtual} aberto.`
          )}>
            + Abrir {MESES[mesAtualNum]}/{anoAtual}
          </button>
        )}
      />
      <TravaEscrituracao clienteId={clienteId} onMudou={() => window.location.reload()} />

      <EncerramentoCarteira clienteId={clienteId} />

      {msg && <Alert tipo={msg.tipo}>{msg.txt}</Alert>}

      <div className="card mb-2">
        <div className="card__title">Períodos contábeis</div>
        {periodos.length === 0 ? (
          <Empty title="Nenhum período" sub="Abra o período do mês atual para começar a escriturar." />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Competência</th>
                <th>Status</th>
                <th>Lançamentos</th>
                <th>Pendentes de aprovação</th>
                <th>Fechado por</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {periodos.map(p => {
                const b = STATUS_BADGE[p.status] || { tipo: 'neutral', label: p.status };
                const fechado = p.status === 'fechado';
                return (
                  <tr key={`${p.ano}-${p.mes}`}>
                    <td className="bold">{MESES[p.mes]}/{p.ano}</td>
                    <td><Badge tipo={b.tipo}>{b.label}</Badge></td>
                    <td>{p.lctos_aprovados} aprovados</td>
                    <td>
                      {p.lctos_pendentes > 0
                        ? <button type="button"
                            onClick={() => {
                              // leva o contexto (ano/mes + status) pra tela de lancamentos
                              try {
                                sessionStorage.setItem('flow_lctos_filtro', JSON.stringify({
                                  ano: p.ano, mes: p.mes, status: 'rascunho',
                                }));
                              } catch {}
                              onNavigate && onNavigate('lancamentos');
                            }}
                            title={`Abrir os ${p.lctos_pendentes} rascunho(s) de ${String(p.mes).padStart(2,'0')}/${p.ano}`}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                              color: '#eab308', font: 'inherit', textDecoration: 'underline',
                              textUnderlineOffset: 3 }}>
                            ⚠ {p.lctos_pendentes} rascunho(s) →
                          </button>
                        : '—'}
                    </td>
                    <td className="small muted">
                      {fechado ? `${p.fechado_por || '—'} · ${fmtDataHora(p.fechado_em)}` : '—'}
                      {p.status === 'reaberto' && p.reaberto_motivo && (
                        <div className="small" title={p.reaberto_motivo}>↺ {p.reaberto_por}: {p.reaberto_motivo.slice(0, 60)}</div>
                      )}
                    </td>
                    <td className="flex">
                      {!fechado && (
                        <button
                          onClick={() => agir(
                            () => fecharPeriodo(clienteId, p.ano, p.mes),
                            `Período ${MESES[p.mes]}/${p.ano} fechado.`
                          )}
                          disabled={p.lctos_pendentes > 0}
                          title={p.lctos_pendentes > 0 ? 'Aprove ou descarte os rascunhos antes de fechar' : 'Fechar período'}
                        >
                          🔒 Fechar
                        </button>
                      )}
                      {fechado && (
                        <button className="ghost" onClick={() => { setReabrindo({ ano: p.ano, mes: p.mes }); setMotivo(''); }}>
                          ↺ Reabrir
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="card__title">Entregas ao cliente (Átrio Connect)</div>
        <p className="small muted" style={{ marginBottom: 10 }}>
          Relatórios congelados (snapshot) só ficam visíveis ao cliente final após liberação explícita.
          Gere snapshots na tela Relatórios.
        </p>
        {snapshots.length === 0 ? (
          <Empty title="Nenhum snapshot" sub="Na tela Relatórios, use 'Congelar versão' após o fechamento do mês." />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Relatório</th>
                <th>Competência</th>
                <th>Gerado</th>
                <th>Liberação</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map(s => (
                <tr key={s.id}>
                  <td className="bold">{s.relatorio}</td>
                  <td>{s.competencia} <span className="small muted">({s.competencia_tipo})</span></td>
                  <td className="small muted">{s.gerado_por} · {fmtDataHora(s.gerado_em)}</td>
                  <td>
                    {s.liberado_cliente
                      ? <Badge tipo="success">Liberado · {fmtDataHora(s.liberado_em)}</Badge>
                      : <Badge tipo="neutral">Interno</Badge>}
                  </td>
                  <td className="flex">
                    {s.liberado_cliente ? (
                      <button className="ghost" onClick={() => agir(
                        () => revogarSnapshot(clienteId, s.id), 'Liberação revogada.'
                      )}>Revogar</button>
                    ) : (
                      <button onClick={() => agir(
                        () => liberarSnapshot(clienteId, s.id), 'Relatório liberado para o cliente.'
                      )}>📤 Liberar p/ cliente</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {reabrindo && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(8,8,10,0.75)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }} onClick={() => setReabrindo(null)}>
          <div className="card" style={{ width: 480, padding: 20 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 8 }}>Reabrir {MESES[reabrindo.mes]}/{reabrindo.ano}</h3>
            <p className="small muted" style={{ marginBottom: 12 }}>
              A reabertura fica registrada com autor, data e motivo. Feche novamente assim que concluir o ajuste.
            </p>
            <textarea
              rows={3}
              style={{ width: '100%', marginBottom: 12 }}
              placeholder="Motivo da reabertura (obrigatório, mín. 5 caracteres)"
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
            />
            <div className="flex" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="ghost" onClick={() => setReabrindo(null)}>Cancelar</button>
              <button className="primary" onClick={confirmarReabertura}>Confirmar reabertura</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
